import clsx from "clsx";
import React, {FunctionComponent, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {CommonInputProps, HTMLTextAreaElementProps, PropsWithOverwrite} from "../../../utils/input";
import {TranslationLibFile} from "../../../utils/locale";
import ButtonLink from "../button/ButtonLink";
import {Action} from "../../../utils/Action";

type TextareaProps = PropsWithOverwrite<HTMLTextAreaElementProps, CommonInputProps<string> & {
    isExpandable?: boolean;
}>;

const Textarea: FunctionComponent<TextareaProps> = ({value, onChange, small, cssClass, isExpandable, ...textareaAttr}) => {
    const {t} = useTranslation(TranslationLibFile.COMMON);
    const containerRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const [minHeight, setMinHeight] = useState(0);
    const [fullHeight, setFullHeight] = useState(0);
    const [isExpanded, setExpanded] = useState(!value);

    const getPixelValue = (value: number) => {
        return `${value}px`;
    };

    // store initial min height
    useEffect(() => {
        if (!containerRef.current || !textareaRef.current || minHeight) {
            return;
        }

        const newMinHeight = containerRef.current.clientHeight;
        textareaRef.current.style.minHeight = getPixelValue(newMinHeight);
        setMinHeight(newMinHeight);
    });

    // compute full height on value change
    useEffect(() => {
        if (!textareaRef.current || !minHeight || !isExpandable) {
            return;
        }

        textareaRef.current.style.height = "auto";

        const newFullHeight = Math.max(minHeight, textareaRef.current.scrollHeight + 2);
        textareaRef.current.style.height = getPixelValue(isExpanded ? newFullHeight : minHeight);
        setFullHeight(newFullHeight);
    }, [minHeight, value, isExpanded]);

    return (
        <div className="input-textarea" ref={containerRef}>
            <textarea
                id={textareaAttr.name}
                value={value}
                ref={textareaRef}
                onChange={(e) => onChange(e.target.value)}
                className={clsx(cssClass, {"input--small": small})}
                {...textareaAttr}
            />
            {(value && isExpandable && fullHeight !== minHeight) &&
                <ButtonLink
                    onClick={() => setExpanded((prevState) => !prevState)}
                    cssClass="input-textarea__link"
                    icon={isExpanded ? Action.COLLAPSE.icon : Action.EXPAND.icon}
                    reverseUnderline
                >
                    {t(isExpanded ? Action.COLLAPSE.labelKey : Action.EXPAND.labelKey)}
                </ButtonLink>
            }
        </div>
    );
};

export default Textarea;
